import type { AuthState } from "@okta/okta-auth-js";
import { configured, idToken, initializeAuth, signIn, signOut } from "./auth";
import "./shell.css";

const app = document.querySelector<HTMLElement>("#app")!;

function escapeHtml(value: unknown) {
  return String(value ?? "").replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", "\"": "&quot;",
  })[character]!);
}

function renderStatus(title: string, copy: string, button?: { label: string; action: () => void }) {
  app.innerHTML = `<section class="status-card"><div class="brand-mark">PT</div><p class="eyebrow">Scale People Team</p><h1>${escapeHtml(title)}</h1><p>${escapeHtml(copy)}</p>${button ? `<button id="status-action">${escapeHtml(button.label)}</button>` : ""}</section>`;
  if (button) document.querySelector("#status-action")?.addEventListener("click", button.action);
}

function renderLogin() {
  app.innerHTML = `
    <section class="login-card">
      <div class="login-visual"><span class="visual-wine">🍷</span><span class="visual-leaf">🌿</span><div><p>Sonoma · July 27–30</p><strong>Connect, align,<br/>and build together.</strong></div></div>
      <div class="login-copy"><div class="brand"><span>PT</span> People Team Offsite</div><p class="eyebrow">Private team itinerary</p><h1>Welcome to Sonoma.</h1><p>Sign in with your corporate Okta account to view the protected agenda, transportation, winery, and activity details.</p><button id="sign-in">Continue with Okta</button><div class="assurances"><span>✓ Group-controlled access</span><span>✓ Session-only tokens</span></div></div>
    </section>`;
  document.querySelector("#sign-in")?.addEventListener("click", () => void signIn());
}

async function renderItinerary(state: AuthState) {
  renderStatus("Loading your itinerary", "Checking your secure session and retrieving the latest offsite details…");
  const token = await idToken();
  if (!token) throw new Error("Your Okta ID token is unavailable. Please sign in again.");
  const response = await fetch("/api/content", {
    headers: { Authorization: `Bearer ${token}` },
    cache: "no-store",
  });
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) throw new Error("Your Okta account is not assigned to this application.");
    throw new Error(`The protected itinerary could not be loaded (${response.status}).`);
  }
  const source = await response.text();
  const parsed = new DOMParser().parseFromString(source, "text/html");
  parsed.querySelectorAll("script").forEach((node) => node.remove());
  parsed.querySelectorAll<HTMLElement>("*").forEach((node) => {
    Array.from(node.attributes).forEach((attribute) => {
      if (/^on/i.test(attribute.name)) node.removeAttribute(attribute.name);
    });
  });
  parsed.querySelectorAll<HTMLAnchorElement>("a[target='_blank']").forEach((link) => {
    link.rel = "noopener noreferrer";
  });

  const claims = state.idToken?.claims;
  const name = escapeHtml(claims?.name || claims?.preferred_username || claims?.email || "Teammate");
  app.innerHTML = `<header class="session-bar"><span>People Team Offsite 2026</span><div><span>${name}</span><button id="sign-out">Sign out</button></div></header><div id="protected-content"></div>`;
  const host = document.querySelector<HTMLElement>("#protected-content")!;
  const shadow = host.attachShadow({ mode: "closed" });
  const style = document.createElement("style");
  const itineraryStyles = Array.from(parsed.querySelectorAll("style")).map((node) => node.textContent).join("\n");
  style.textContent = `${itineraryStyles.replace(/(^|})\s*body\s*\{/g, "$1 :host{")}\n:host{display:block}.tabs{top:46px!important}`;
  const content = document.createElement("div");
  content.innerHTML = parsed.body.innerHTML;
  shadow.append(style, content);

  const buttons = Array.from(shadow.querySelectorAll<HTMLButtonElement>(".tab-btn"));
  const panels = Array.from(shadow.querySelectorAll<HTMLElement>(".panel"));
  buttons.forEach((button) => button.addEventListener("click", () => {
    const index = buttons.indexOf(button);
    buttons.forEach((item) => item.classList.remove("active"));
    panels.forEach((panel) => panel.classList.remove("active"));
    button.classList.add("active");
    panels[index]?.classList.add("active");
  }));
  document.querySelector("#sign-out")?.addEventListener("click", () => void signOut());
}

async function start() {
  if (!configured) {
    renderStatus("Connect the Okta SPA", "Set VITE_OKTA_ISSUER, VITE_OKTA_CLIENT_ID, and VITE_OKTA_AUDIENCE before building the site.");
    return;
  }
  renderStatus("Restoring your secure session", "One moment while we check Okta…");
  try {
    await initializeAuth((state) => {
      if (state.isAuthenticated) void renderItinerary(state).catch(showError);
      else renderLogin();
    });
  } catch (error) {
    showError(error);
  }
}

function showError(error: unknown) {
  renderStatus("We couldn’t open the itinerary", error instanceof Error ? error.message : "Authentication could not be completed.", { label: "Try Okta again", action: () => void signIn() });
}

void start();
